/*
 * Amana Labs — Organic motion engine
 *
 * A progressively enhanced, dependency-free interaction layer for Shopify.
 * Motion is deliberately limited to transforms, opacity and CSS custom
 * properties. Content remains visible when JavaScript, rich motion or pointer
 * input is unavailable.
 */
(function () {
  "use strict";

  if (typeof window === "undefined" || typeof document === "undefined") return;

  var RUNTIME_KEY = "__AMANA_ORGANIC_MOTION_RUNTIME__";
  var previousRuntime = window[RUNTIME_KEY];
  if (previousRuntime && typeof previousRuntime.destroy === "function") {
    previousRuntime.destroy();
  }

  var html = document.documentElement;
  var reducedQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  var finePointerQuery = window.matchMedia("(hover: hover) and (pointer: fine)");
  var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;

  var SCENE_SELECTOR = [
    "[data-amana-scene]",
    "[data-amana-section]",
    ".amana-hero",
    ".amana-page-hero",
    ".amana-method",
    ".amana-journal-preview",
    ".amana-blog",
    ".amana-circle",
    ".amana-signup",
    ".amana-article",
    ".amana-footer"
  ].join(",");

  var DEPTH_SELECTOR = [
    "[data-amana-depth]",
    "[data-amana-hero-halo]",
    "[data-amana-hero-botanical]"
  ].join(",");

  var THREAD_SELECTOR = [
    "[data-amana-thread-path]",
    ".amana-thread__path",
    ".amana-thread path",
    ".amana-living-thread path"
  ].join(",");

  var POINTER_SCENE_SELECTOR = [
    "[data-amana-pointer-scene]",
    ".amana-hero",
    ".amana-page-hero",
    ".amana-method",
    ".amana-journal-preview",
    ".amana-blog",
    ".amana-article"
  ].join(",");

  var MAGNETIC_SELECTOR = [
    "[data-amana-magnetic]",
    ".amana-button",
    ".amana-next-step__gateway"
  ].join(",");

  var INSPECT_SELECTOR = [
    "[data-amana-inspect]",
    ".amana-principle-card",
    ".amana-journal-card",
    ".amana-method__step",
    ".amana-dossier-column"
  ].join(",");

  var REVEAL_SELECTOR = "[data-reveal], [data-amana-reveal]";
  var JOURNAL_CARD_SELECTOR = ".amana-journal-card, [data-amana-journal-item]";
  var FORM_CONTROL_SELECTOR = [
    "input:not([type='hidden']):not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])"
  ].join(",");

  var OWNED_STYLE_PROPERTIES = [
    "--amana-scene-progress",
    "--amana-local-progress",
    "--amana-thread-progress",
    "--amana-pointer-x",
    "--amana-pointer-y",
    "--amana-pointer-offset-x",
    "--amana-pointer-offset-y",
    "--amana-light-x",
    "--amana-light-y",
    "--amana-light-shift-x",
    "--amana-light-shift-y",
    "--amana-depth-factor",
    "--amana-depth-x",
    "--amana-depth-y",
    "--amana-magnetic-x",
    "--amana-magnetic-y",
    "--amana-inspect-x",
    "--amana-inspect-y",
    "--amana-method-active-step",
    "--amana-method-step-progress",
    "--amana-phase-progress",
    "--amana-circle-progress",
    "--amana-form-progress",
    "--amana-signup-progress",
    "--amana-reading-progress",
    "--amana-organic-delay",
    "--amana-journal-index"
  ];

  function toArray(value) {
    return Array.prototype.slice.call(value || []);
  }

  function queryAll(scope, selector) {
    return toArray((scope || document).querySelectorAll(selector));
  }

  function uniqueElements(elements) {
    return elements.filter(function (element, index, all) {
      return element && all.indexOf(element) === index;
    });
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function parseBoolean(value, fallback) {
    if (value === null || typeof value === "undefined" || value === "") return Boolean(fallback);
    return !/^(false|0|off|no)$/i.test(String(value));
  }

  function parseNumber(value, fallback) {
    var number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function normaliseToken(value, fallback) {
    var token = String(value || fallback || "scene")
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
    return token || fallback || "scene";
  }

  function isElementVisible(element) {
    if (!element || element.hidden || !element.isConnected) return false;
    var style = window.getComputedStyle(element);
    return style.display !== "none" && style.visibility !== "hidden" && element.getClientRects().length > 0;
  }

  function closestElement(target, selector) {
    return target && target.nodeType === 1 && typeof target.closest === "function"
      ? target.closest(selector)
      : null;
  }

  function addListener(cleanups, target, type, listener, options) {
    if (!target || typeof target.addEventListener !== "function") return;
    target.addEventListener(type, listener, options);
    cleanups.push(function () {
      target.removeEventListener(type, listener, options);
    });
  }

  function addMediaListener(cleanups, mediaQuery, listener) {
    if (!mediaQuery) return;
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", listener);
      cleanups.push(function () {
        mediaQuery.removeEventListener("change", listener);
      });
      return;
    }
    if (typeof mediaQuery.addListener === "function") {
      mediaQuery.addListener(listener);
      cleanups.push(function () {
        mediaQuery.removeListener(listener);
      });
    }
  }

  function removeOwnedStyles(element) {
    if (!element || !element.style) return;
    OWNED_STYLE_PROPERTIES.forEach(function (property) {
      element.style.removeProperty(property);
    });
  }

  function springStep(state, targetX, targetY, delta, stiffness, damping) {
    state.vx += ((targetX - state.x) * stiffness - state.vx * damping) * delta;
    state.vy += ((targetY - state.y) * stiffness - state.vy * damping) * delta;
    state.x += state.vx * delta;
    state.y += state.vy * delta;

    var settled =
      Math.abs(targetX - state.x) < 0.0008 &&
      Math.abs(targetY - state.y) < 0.0008 &&
      Math.abs(state.vx) < 0.004 &&
      Math.abs(state.vy) < 0.004;

    if (settled) {
      state.x = targetX;
      state.y = targetY;
      state.vx = 0;
      state.vy = 0;
    }
    return !settled;
  }

  function getPolicy() {
    var body = document.body;
    var cores = parseNumber(navigator.hardwareConcurrency, 0);
    var memory = parseNumber(navigator.deviceMemory, 0);
    var saveData = Boolean(connection && connection.saveData);
    var lowPower =
      (memory > 0 && memory <= 2) ||
      (memory === 0 && cores > 0 && cores <= 4);
    var designMode = Boolean(window.Shopify && window.Shopify.designMode);
    var organicGate = Boolean(body && parseBoolean(body.getAttribute("data-amana-organic-motion"), false));
    var pointerGate = Boolean(body && parseBoolean(body.getAttribute("data-amana-pointer-response"), false));
    var reduced = reducedQuery.matches;
    var motionAllowed = organicGate && !reduced;
    var continuousAllowed = motionAllowed && !saveData && !lowPower && !designMode;

    return {
      organicGate: organicGate,
      pointerGate: pointerGate,
      reduced: reduced,
      saveData: saveData,
      lowPower: lowPower,
      designMode: designMode,
      motionAllowed: motionAllowed,
      continuousAllowed: continuousAllowed,
      pointerAllowed: continuousAllowed && pointerGate && finePointerQuery.matches
    };
  }

  function sceneNameFor(element, index) {
    var explicit = element.getAttribute("data-amana-scene") || element.getAttribute("data-amana-section");
    if (explicit) return normaliseToken(explicit, "scene-" + index);
    var knownClasses = [
      "hero",
      "page-hero",
      "method",
      "journal-preview",
      "blog",
      "circle",
      "signup",
      "article",
      "footer"
    ];
    for (var i = 0; i < knownClasses.length; i += 1) {
      if (element.classList.contains("amana-" + knownClasses[i])) return knownClasses[i];
    }
    return "scene-" + index;
  }

  function sceneToneFor(element) {
    var explicit = element.getAttribute("data-amana-tone");
    if (explicit) return normaliseToken(explicit, "night");
    if (element.getAttribute("data-theme") === "light") return "paper";
    if (
      element.classList.contains("amana-surface--paper") ||
      element.classList.contains("amana-surface--paper-soft")
    ) return "paper";
    if (element.classList.contains("amana-surface--lapis")) return "lapis";
    return "night";
  }

  function routeGroupForScene(sceneName) {
    var name = String(sceneName || "");
    if (/journal|reading|article|blog/.test(name)) return "journal";
    if (/formulation|dossier/.test(name)) return "formulation";
    if (/method|evidence|preuve/.test(name)) return "method";
    if (/circle|cercle|signup|join|rejoindre/.test(name)) return "join";
    if (/amanah|approach|approche|principle|roots|why/.test(name)) return "approach";
    if (/hero|home|accueil/.test(name)) return "home";
    return "";
  }

  function linkMatchesGroup(link, group, activeScene) {
    if (!link || !group) return false;
    var rawHref = link.getAttribute("href") || "";
    var label = String(link.textContent || "").trim().toLowerCase();

    if (rawHref.charAt(0) === "#") {
      var target = document.getElementById(rawHref.slice(1));
      if (target && activeScene && (activeScene.root.contains(target) || target.contains(activeScene.root))) return true;
    }

    var path = "";
    try {
      path = new URL(rawHref, window.location.href).pathname.toLowerCase();
    } catch (_error) {
      path = rawHref.toLowerCase();
    }

    if (group === "home") return path === "/" || label.indexOf("amana labs") !== -1;
    if (group === "approach") return /\/pages\/approche(?:\/|$)/.test(path) || label.indexOf("approche") !== -1;
    if (group === "method") return /\/pages\/methode(?:\/|$)/.test(path) || label.indexOf("méthode") !== -1 || label.indexOf("methode") !== -1;
    if (group === "formulation") return /\/pages\/formulation-01(?:\/|$)/.test(path) || label.indexOf("formulation") !== -1;
    if (group === "journal") return /\/blogs\//.test(path) || label.indexOf("journal") !== -1;
    if (group === "join") return /\/pages\/rejoindre(?:\/|$)/.test(path) || label.indexOf("rejoindre") !== -1 || label.indexOf("cercle") !== -1;
    return false;
  }

  function revealVariantFor(element) {
    var explicit = element.getAttribute("data-amana-reveal-variant") || element.getAttribute("data-amana-reveal");
    if (explicit && explicit !== "true") return normaliseToken(explicit, "soft");
    if (element.matches("h1, h2, .amana-title--xl, .amana-title--lg, .amana-section-heading")) return "title";
    if (element.matches(".amana-eyebrow, .amana-divider")) return "line";
    if (element.matches(".amana-manuscript, blockquote")) return "manuscript";
    if (element.matches("article, li, .amana-journal-card, .amana-principle-card, .amana-dossier-column")) return "card";
    return "soft";
  }

  function AmanaOrganicMotion() {
    this.cleanups = [];
    this.destroyed = false;
    this.policy = getPolicy();
    this.frameId = 0;
    this.lastFrameTime = 0;
    this.scrollDirty = true;
    this.pointerAnimating = false;
    this.magneticAnimating = false;
    this.sceneObserver = null;
    this.revealObserver = null;
    this.resizeObserver = null;
    this.stageObserver = null;
    this.scenes = [];
    this.sceneByRoot = new Map();
    this.activeScene = null;
    this.pointerScene = null;
    this.activeMagnetic = null;
    this.magneticStates = new Map();
    this.activeInspect = null;
    this.journalHoverCard = null;
    this.journalFocusCard = null;
    this.journalRoots = [];
    this.methodRoots = [];
    this.circleRoots = [];
    this.formRoots = [];
    this.revealElements = [];
    this.headers = [];
    this.headerLinks = [];
    this.threadPaths = [];
    this.modifiedRevealAttributes = [];
    this.pathSnapshots = [];
    this.progressSnapshots = [];

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerOut = this.onPointerOut.bind(this);
    this.onPointerOver = this.onPointerOver.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.frame = this.frame.bind(this);

    this.applyPolicyClasses();
    this.discover();
    this.prepareStaticStates();

    if (!this.policy.organicGate) return;

    this.setupScenes();
    this.setupReveals();
    this.setupInteractions();
    this.setupLifecycle();
    this.requestFrame();
  }

  AmanaOrganicMotion.prototype.applyPolicyClasses = function () {
    html.classList.toggle("amana-organic-enabled", this.policy.organicGate);
    html.classList.toggle("amana-organic-reduced", this.policy.reduced);
    html.classList.toggle("amana-organic-static", !this.policy.continuousAllowed);
    html.classList.toggle("amana-organic-pointer-ready", this.policy.pointerAllowed);
    html.classList.toggle("amana-organic-design-mode", this.policy.designMode);
  };

  AmanaOrganicMotion.prototype.discover = function () {
    var controller = this;
    var sceneElements = uniqueElements(queryAll(document, SCENE_SELECTOR)).filter(function (element) {
      var closest = element.closest("[data-amana-scene], [data-amana-section]");
      return !closest || closest === element || element.hasAttribute("data-amana-scene");
    });

    this.scenes = sceneElements.map(function (root, index) {
      var depthElements = uniqueElements(queryAll(root, DEPTH_SELECTOR));
      var lights = uniqueElements(queryAll(root, "[data-amana-pointer-light], .amana-pointer-light"));
      var paths = uniqueElements(queryAll(root, THREAD_SELECTOR));
      var state = {
        root: root,
        name: sceneNameFor(root, index),
        tone: sceneToneFor(root),
        ratio: 0,
        intersecting: false,
        depthElements: depthElements,
        lights: lights,
        paths: paths,
        pointer: { x: 0, y: 0, vx: 0, vy: 0, tx: 0, ty: 0 },
        pointerCapable: root.matches(POINTER_SCENE_SELECTOR)
      };
      controller.sceneByRoot.set(root, state);
      return state;
    });

    this.headers = queryAll(document, "[data-amana-header]");
    this.headerLinks = uniqueElements(this.headers.reduce(function (links, header) {
      return links.concat(queryAll(header, "a[href]"));
    }, []));
    this.threadPaths = uniqueElements(this.scenes.reduce(function (paths, scene) {
      return paths.concat(scene.paths);
    }, []));
    this.revealElements = uniqueElements(queryAll(document, REVEAL_SELECTOR));
    this.methodRoots = uniqueElements(queryAll(document, "[data-amana-method], .amana-method"));
    this.circleRoots = uniqueElements(queryAll(document, "[data-amana-scene='circle'], [data-amana-section='circle'], .amana-circle"));
    this.formRoots = uniqueElements(queryAll(document, "[data-amana-signup], .amana-signup"));
    this.journalRoots = this.scenes.filter(function (scene) {
      return /journal|blog/.test(scene.name);
    }).map(function (scene) {
      return scene.root;
    });

    queryAll(document, "[data-amana-phase-progress], [data-amana-circle-progress], [data-amana-signup-progress]").forEach(function (progress) {
      controller.progressSnapshots.push({
        element: progress,
        valueNow: progress.getAttribute("aria-valuenow")
      });
    });
  };

  AmanaOrganicMotion.prototype.prepareStaticStates = function () {
    var controller = this;
    var intensity = document.body ? document.body.getAttribute("data-amana-motion-intensity") : "balanced";
    var maxDepth = intensity === "subtle" ? 8 : intensity === "expressive" ? 14 : 11;
    this.maxDepth = maxDepth;

    this.scenes.forEach(function (scene) {
      scene.root.style.setProperty("--amana-pointer-x", "50%");
      scene.root.style.setProperty("--amana-pointer-y", "50%");
      scene.root.style.setProperty("--amana-pointer-offset-x", "0px");
      scene.root.style.setProperty("--amana-pointer-offset-y", "0px");
      scene.depthElements.forEach(function (element) {
        var fallback = element.matches("[data-amana-hero-botanical]") ? 0.18 : -0.08;
        var factor = clamp(parseNumber(element.getAttribute("data-amana-depth"), fallback), -1, 1);
        element.__amanaDepthFactor = factor;
        element.style.setProperty("--amana-depth-factor", String(factor));
        element.style.setProperty("--amana-depth-x", "0px");
        element.style.setProperty("--amana-depth-y", "0px");
      });
      scene.lights.forEach(function (light) {
        light.style.setProperty("--amana-pointer-x", "50%");
        light.style.setProperty("--amana-pointer-y", "50%");
        light.style.setProperty("--amana-light-x", "50%");
        light.style.setProperty("--amana-light-y", "50%");
        light.style.setProperty("--amana-light-shift-x", "0px");
        light.style.setProperty("--amana-light-shift-y", "0px");
      });
    });

    this.threadPaths.forEach(function (path) {
      controller.pathSnapshots.push({
        path: path,
        pathLength: path.getAttribute("pathLength"),
        dasharray: path.style.strokeDasharray,
        dashoffset: path.style.strokeDashoffset
      });
      if (!path.hasAttribute("pathLength")) path.setAttribute("pathLength", "1");
      path.style.strokeDasharray = "1";
      path.style.strokeDashoffset = controller.policy.continuousAllowed ? "1" : "0";
    });

    queryAll(document, MAGNETIC_SELECTOR).forEach(function (element) {
      element.style.setProperty("--amana-magnetic-x", "0px");
      element.style.setProperty("--amana-magnetic-y", "0px");
    });

    if (!this.policy.motionAllowed) {
      this.revealElements.forEach(function (element) {
        element.classList.add("is-amana-revealed");
      });
    }
  };

  AmanaOrganicMotion.prototype.setupScenes = function () {
    var controller = this;
    if ("IntersectionObserver" in window) {
      this.sceneObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var scene = controller.sceneByRoot.get(entry.target);
          if (!scene) return;
          scene.ratio = entry.intersectionRatio;
          scene.intersecting = entry.isIntersecting;
          scene.root.classList.toggle("is-amana-scene-visible", entry.isIntersecting);
        });
        controller.scrollDirty = true;
        controller.requestFrame();
      }, {
        rootMargin: "-8% 0px -12% 0px",
        threshold: [0, 0.08, 0.2, 0.35, 0.5, 0.7, 0.9, 1]
      });
      this.scenes.forEach(function (scene) {
        controller.sceneObserver.observe(scene.root);
      });
    } else {
      this.scenes.forEach(function (scene) {
        scene.intersecting = true;
        scene.ratio = 1;
      });
    }
  };

  AmanaOrganicMotion.prototype.setupReveals = function () {
    var controller = this;
    if (!this.policy.continuousAllowed || !("IntersectionObserver" in window)) {
      this.revealElements.forEach(function (element) {
        element.classList.add("is-amana-revealed");
      });
      return;
    }

    var revealLine = window.innerHeight * 0.94;
    this.revealElements.forEach(function (element, index) {
      if (!element.hasAttribute("data-amana-reveal-variant")) {
        controller.modifiedRevealAttributes.push(element);
        element.setAttribute("data-amana-reveal-variant", revealVariantFor(element));
      }
      if (!element.style.getPropertyValue("--amana-organic-delay")) {
        var authored = parseInt(element.style.getPropertyValue("--reveal-delay"), 10);
        var delay = Number.isFinite(authored) ? Math.min(authored, 360) : (index % 4) * 65;
        element.style.setProperty("--amana-organic-delay", delay + "ms");
      }
      var rect = element.getBoundingClientRect();
      if (rect.top <= revealLine && rect.bottom >= 0) element.classList.add("is-amana-revealed");
    });

    this.revealObserver = new IntersectionObserver(function (entries, observer) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-amana-revealed");
        observer.unobserve(entry.target);
      });
    }, {
      rootMargin: "0px 0px -7% 0px",
      threshold: 0.04
    });

    this.revealElements.forEach(function (element) {
      if (!element.classList.contains("is-amana-revealed")) controller.revealObserver.observe(element);
    });

    // The gating class is added only after above-fold content has been made visible.
    html.classList.add("amana-organic-ready");
  };

  AmanaOrganicMotion.prototype.setupInteractions = function () {
    addListener(this.cleanups, document, "pointerover", this.onPointerOver, true);
    addListener(this.cleanups, document, "focusin", this.onFocusIn, true);
    addListener(this.cleanups, document, "focusout", this.onFocusOut, true);
    addListener(this.cleanups, document, "input", this.onInput, true);
    addListener(this.cleanups, document, "change", this.onInput, true);

    if (!this.policy.pointerAllowed) return;
    addListener(this.cleanups, document, "pointermove", this.onPointerMove, { passive: true });
    addListener(this.cleanups, document, "pointerout", this.onPointerOut, { passive: true });
    addListener(this.cleanups, window, "blur", this.onPointerOut);

    queryAll(document, MAGNETIC_SELECTOR).forEach(function (element) {
      element.classList.add("is-amana-magnetic-ready");
    });
  };

  AmanaOrganicMotion.prototype.setupLifecycle = function () {
    var controller = this;
    addListener(this.cleanups, window, "scroll", this.onScroll, { passive: true });
    addListener(this.cleanups, window, "resize", this.onResize, { passive: true });
    addListener(this.cleanups, document, "visibilitychange", this.onVisibilityChange);

    if ("ResizeObserver" in window && document.body) {
      this.resizeObserver = new ResizeObserver(function () {
        if (controller.destroyed) return;
        controller.scrollDirty = true;
        controller.requestFrame();
      });
      this.resizeObserver.observe(document.body);
      this.scenes.forEach(function (scene) {
        controller.resizeObserver.observe(scene.root);
      });
    }

    this.formRoots.forEach(function (signup) {
      controller.syncSignupProgress(signup);
    });
    if ("MutationObserver" in window && this.formRoots.length) {
      this.stageObserver = new MutationObserver(function (records) {
        if (controller.destroyed) return;
        var roots = [];
        records.forEach(function (record) {
          var signup = closestElement(record.target, "[data-amana-signup], .amana-signup");
          if (signup && roots.indexOf(signup) === -1) roots.push(signup);
        });
        roots.forEach(function (signup) {
          controller.syncSignupProgress(signup);
        });
      });
      this.formRoots.forEach(function (signup) {
        controller.stageObserver.observe(signup, {
          subtree: true,
          attributes: true,
          attributeFilter: ["hidden", "aria-hidden", "data-amana-signup-state"]
        });
      });
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        if (!controller.destroyed) {
          controller.scrollDirty = true;
          controller.requestFrame();
        }
      });
    }
  };

  AmanaOrganicMotion.prototype.onScroll = function () {
    this.scrollDirty = true;
    this.requestFrame();
  };

  AmanaOrganicMotion.prototype.onResize = function () {
    this.scrollDirty = true;
    this.requestFrame();
  };

  AmanaOrganicMotion.prototype.sceneForPointerTarget = function (target) {
    var root = closestElement(target, POINTER_SCENE_SELECTOR);
    if (!root) return null;
    if (this.sceneByRoot.has(root)) return this.sceneByRoot.get(root);
    var sceneRoot = root.closest(SCENE_SELECTOR);
    return sceneRoot ? this.sceneByRoot.get(sceneRoot) || null : null;
  };

  AmanaOrganicMotion.prototype.setPointerScene = function (scene) {
    if (this.pointerScene === scene) return;
    if (this.pointerScene) {
      this.pointerScene.pointer.tx = 0;
      this.pointerScene.pointer.ty = 0;
      this.pointerScene.root.classList.remove("is-amana-pointer-active");
    }
    this.pointerScene = scene;
    if (scene) scene.root.classList.add("is-amana-pointer-active");
    this.pointerAnimating = true;
  };

  AmanaOrganicMotion.prototype.setActiveMagnetic = function (element, clientX, clientY) {
    if (this.activeMagnetic !== element) {
      if (this.activeMagnetic) {
        var oldState = this.magneticStates.get(this.activeMagnetic);
        if (oldState) {
          oldState.tx = 0;
          oldState.ty = 0;
        }
        this.activeMagnetic.classList.remove("is-amana-magnetic-active");
      }
      this.activeMagnetic = element;
      if (element) element.classList.add("is-amana-magnetic-active");
    }

    if (!element) {
      this.magneticAnimating = true;
      return;
    }

    var state = this.magneticStates.get(element);
    if (!state) {
      state = { element: element, x: 0, y: 0, vx: 0, vy: 0, tx: 0, ty: 0 };
      this.magneticStates.set(element, state);
    }
    var rect = element.getBoundingClientRect();
    var nx = rect.width ? (clientX - (rect.left + rect.width / 2)) / (rect.width / 2) : 0;
    var ny = rect.height ? (clientY - (rect.top + rect.height / 2)) / (rect.height / 2) : 0;
    state.tx = clamp(nx, -1, 1) * 6;
    state.ty = clamp(ny, -1, 1) * 6;
    this.magneticAnimating = true;
  };

  AmanaOrganicMotion.prototype.onPointerMove = function (event) {
    if (!this.policy.pointerAllowed || (event.pointerType && event.pointerType !== "mouse" && event.pointerType !== "pen")) return;

    var scene = this.sceneForPointerTarget(event.target);
    this.setPointerScene(scene);
    if (scene) {
      var rect = scene.root.getBoundingClientRect();
      scene.pointer.tx = rect.width
        ? clamp((event.clientX - (rect.left + rect.width / 2)) / (rect.width / 2), -1, 1)
        : 0;
      scene.pointer.ty = rect.height
        ? clamp((event.clientY - (rect.top + rect.height / 2)) / (rect.height / 2), -1, 1)
        : 0;
      this.pointerAnimating = true;

      var inspect = closestElement(event.target, INSPECT_SELECTOR);
      if (inspect && scene.root.contains(inspect)) {
        var inspectRect = inspect.getBoundingClientRect();
        var inspectX = inspectRect.width ? clamp((event.clientX - inspectRect.left) / inspectRect.width, 0, 1) : 0.5;
        var inspectY = inspectRect.height ? clamp((event.clientY - inspectRect.top) / inspectRect.height, 0, 1) : 0.5;
        inspect.style.setProperty("--amana-inspect-x", (inspectX * 100).toFixed(2) + "%");
        inspect.style.setProperty("--amana-inspect-y", (inspectY * 100).toFixed(2) + "%");
      }
    }

    var magnetic = closestElement(event.target, MAGNETIC_SELECTOR);
    if (magnetic && (magnetic.matches(":disabled") || magnetic.getAttribute("aria-disabled") === "true")) magnetic = null;
    this.setActiveMagnetic(magnetic, event.clientX, event.clientY);
    this.requestFrame();
  };

  AmanaOrganicMotion.prototype.onPointerOut = function (event) {
    if (event && event.type === "pointerout" && event.relatedTarget) return;
    this.setPointerScene(null);
    this.setActiveMagnetic(null, 0, 0);
    this.setInspect(null, "pointer");
    this.journalHoverCard = null;
    this.syncJournalCard();
    this.requestFrame();
  };

  AmanaOrganicMotion.prototype.setInspect = function (element, source) {
    if (source === "focus") {
      if (element) element.setAttribute("data-amana-inspect-focus", "true");
      queryAll(document, "[data-amana-inspect-focus='true']").forEach(function (candidate) {
        if (candidate !== element) candidate.removeAttribute("data-amana-inspect-focus");
      });
    }

    if (this.activeInspect === element) return;
    if (this.activeInspect && this.activeInspect.getAttribute("data-amana-inspect-focus") !== "true") {
      this.activeInspect.classList.remove("is-amana-inspected");
    }
    this.activeInspect = element;
    if (element) element.classList.add("is-amana-inspected");
  };

  AmanaOrganicMotion.prototype.onPointerOver = function (event) {
    if (!this.policy.pointerAllowed) return;
    var inspect = closestElement(event.target, INSPECT_SELECTOR);
    if (inspect && (!event.relatedTarget || !inspect.contains(event.relatedTarget))) {
      this.setInspect(inspect, "pointer");
    } else if (!inspect && this.activeInspect && this.activeInspect.getAttribute("data-amana-inspect-focus") !== "true") {
      this.setInspect(null, "pointer");
    }

    var card = closestElement(event.target, JOURNAL_CARD_SELECTOR);
    if (card !== this.journalHoverCard) {
      this.journalHoverCard = card || null;
      this.syncJournalCard();
    }
  };

  AmanaOrganicMotion.prototype.onFocusIn = function (event) {
    var inspect = closestElement(event.target, INSPECT_SELECTOR);
    if (inspect) this.setInspect(inspect, "focus");

    var card = closestElement(event.target, JOURNAL_CARD_SELECTOR);
    if (card) {
      this.journalFocusCard = card;
      this.syncJournalCard();
    }

    var signup = closestElement(event.target, "[data-amana-signup], .amana-signup");
    if (signup) this.updateFormFocus(signup, event.target);

    var method = closestElement(event.target, "[data-amana-method], .amana-method");
    var step = closestElement(event.target, "[data-amana-method-step], .amana-method__step");
    if (method && step) this.setMethodStep(method, step, true);
  };

  AmanaOrganicMotion.prototype.onFocusOut = function (event) {
    var inspect = closestElement(event.target, INSPECT_SELECTOR);
    var nextInspect = closestElement(event.relatedTarget, INSPECT_SELECTOR);
    if (inspect && inspect !== nextInspect) {
      inspect.removeAttribute("data-amana-inspect-focus");
      inspect.classList.remove("is-amana-inspected");
      if (this.activeInspect === inspect) this.activeInspect = null;
    }

    var card = closestElement(event.target, JOURNAL_CARD_SELECTOR);
    var nextCard = closestElement(event.relatedTarget, JOURNAL_CARD_SELECTOR);
    if (card && card !== nextCard) {
      this.journalFocusCard = nextCard;
      this.syncJournalCard();
    }

    var signup = closestElement(event.target, "[data-amana-signup], .amana-signup");
    if (signup && (!event.relatedTarget || !signup.contains(event.relatedTarget))) {
      signup.classList.remove("has-amana-form-focus");
      signup.removeAttribute("data-amana-form-focus");
    }

    var method = closestElement(event.target, "[data-amana-method], .amana-method");
    var nextMethodStep = closestElement(event.relatedTarget, "[data-amana-method-step], .amana-method__step");
    if (method && (!nextMethodStep || !method.contains(nextMethodStep))) {
      method.classList.remove("has-amana-focus-step");
      this.scrollDirty = true;
      this.requestFrame();
    }
  };

  AmanaOrganicMotion.prototype.onInput = function (event) {
    var signup = closestElement(event.target, "[data-amana-signup], .amana-signup");
    if (signup) this.updateFormFocus(signup, event.target);
  };

  AmanaOrganicMotion.prototype.updateFormFocus = function (signup, target) {
    var controls = queryAll(signup, FORM_CONTROL_SELECTOR).filter(isElementVisible);
    var control = closestElement(target, FORM_CONTROL_SELECTOR);
    var index = controls.indexOf(control);
    if (index < 0 || !controls.length) return;
    var progress = clamp((index + 1) / controls.length, 0, 1);
    signup.style.setProperty("--amana-form-progress", progress.toFixed(5));
    signup.setAttribute("data-amana-form-focus", String(index + 1));
    signup.classList.add("has-amana-form-focus");
    var form = control.closest("form");
    if (form) form.style.setProperty("--amana-form-progress", progress.toFixed(5));
  };

  AmanaOrganicMotion.prototype.syncSignupProgress = function (signup) {
    if (!signup) return;
    var progressbar = signup.querySelector("[data-amana-signup-progress]");
    var stages = queryAll(signup, "[data-amana-progress-step]");
    var activeStage = stages.find(function (stage) {
      return !stage.hidden && stage.getAttribute("aria-hidden") !== "true";
    }) || stages[0];
    var max = progressbar ? Math.max(1, parseNumber(progressbar.getAttribute("aria-valuemax"), 3)) : 3;
    var step = activeStage
      ? clamp(parseNumber(activeStage.getAttribute("data-amana-progress-step"), 1), 1, max)
      : 1;
    var progress = clamp(step / max, 0, 1);

    signup.style.setProperty("--amana-signup-progress", progress.toFixed(5));
    signup.setAttribute("data-amana-signup-progress-step", String(step));
    if (progressbar) {
      progressbar.setAttribute("aria-valuenow", String(step));
      progressbar.style.setProperty("--amana-signup-progress", progress.toFixed(5));
      var fill = progressbar.querySelector("[data-amana-signup-progress-fill]");
      if (fill) fill.style.setProperty("--amana-signup-progress", progress.toFixed(5));
    }
  };

  AmanaOrganicMotion.prototype.syncJournalCard = function () {
    var active = this.journalFocusCard || this.journalHoverCard;
    this.journalRoots.forEach(function (root) {
      var cards = queryAll(root, JOURNAL_CARD_SELECTOR);
      var rootActive = active && root.contains(active) ? active : null;
      var activeIndex = rootActive ? cards.indexOf(rootActive) : -1;
      root.classList.toggle("has-amana-journal-preview", activeIndex >= 0);
      if (activeIndex >= 0) {
        root.setAttribute("data-amana-journal-active", String(activeIndex + 1));
        root.style.setProperty("--amana-journal-index", String(activeIndex));
      } else {
        root.removeAttribute("data-amana-journal-active");
        root.style.removeProperty("--amana-journal-index");
      }
      cards.forEach(function (card, index) {
        card.classList.toggle("is-amana-preview-active", index === activeIndex);
        card.classList.toggle("is-amana-preview-muted", activeIndex >= 0 && index !== activeIndex);
      });
    });
  };

  AmanaOrganicMotion.prototype.setMethodStep = function (method, activeStep, fromFocus) {
    var steps = queryAll(method, "[data-amana-method-step], .amana-method__step");
    var index = steps.indexOf(activeStep);
    if (index < 0) return;
    steps.forEach(function (step, stepIndex) {
      step.classList.toggle("is-amana-scroll-active", stepIndex === index);
      step.classList.toggle("is-amana-current", stepIndex === index);
    });
    method.setAttribute("data-amana-active-step", String(index + 1));
    method.style.setProperty("--amana-method-active-step", String(index));
    var progress = (index + 1) / Math.max(1, steps.length);
    method.style.setProperty("--amana-method-step-progress", progress.toFixed(5));
    method.style.setProperty("--amana-phase-progress", progress.toFixed(5));
    var progressbar = method.querySelector("[data-amana-phase-progress]");
    if (progressbar) {
      progressbar.setAttribute("aria-valuenow", String(index + 1));
      progressbar.style.setProperty("--amana-phase-progress", progress.toFixed(5));
      var fill = progressbar.querySelector("[data-amana-phase-progress-fill]");
      if (fill) fill.style.setProperty("--amana-phase-progress", progress.toFixed(5));
    }
    method.classList.toggle("has-amana-focus-step", Boolean(fromFocus));
  };

  AmanaOrganicMotion.prototype.requestFrame = function () {
    if (this.destroyed || this.frameId || document.hidden) return;
    this.frameId = window.requestAnimationFrame(this.frame);
  };

  AmanaOrganicMotion.prototype.frame = function (timestamp) {
    this.frameId = 0;
    if (this.destroyed || document.hidden) return;

    var elapsed = this.lastFrameTime ? (timestamp - this.lastFrameTime) / 1000 : 1 / 60;
    var delta = clamp(elapsed, 1 / 120, 1 / 25);
    this.lastFrameTime = timestamp;

    if (this.scrollDirty) {
      this.scrollDirty = false;
      this.updateScrollState();
    }

    var keepPointer = this.policy.pointerAllowed && this.updatePointerSprings(delta);
    var keepMagnetic = this.policy.pointerAllowed && this.updateMagneticSprings(delta);
    this.pointerAnimating = keepPointer;
    this.magneticAnimating = keepMagnetic;

    if (keepPointer || keepMagnetic || this.scrollDirty) this.requestFrame();
    else this.lastFrameTime = 0;
  };

  AmanaOrganicMotion.prototype.updateScrollState = function () {
    if (!this.scenes.length) return;
    var viewportHeight = Math.max(1, window.innerHeight);
    var headerHeight = this.headers.length ? this.headers[0].getBoundingClientRect().height : 0;
    var probe = headerHeight + (viewportHeight - headerHeight) * 0.34;

    // Read phase.
    var revealReads = this.revealElements.filter(function (element) {
      return !element.classList.contains("is-amana-revealed");
    }).map(function (element) {
      return { element: element, rect: element.getBoundingClientRect() };
    });

    var sceneReads = this.scenes.map(function (scene) {
      var rect = scene.root.getBoundingClientRect();
      var progress = clamp((viewportHeight - rect.top) / Math.max(1, viewportHeight + rect.height), 0, 1);
      return {
        scene: scene,
        rect: rect,
        progress: progress,
        distance: rect.top <= probe && rect.bottom >= probe
          ? 0
          : Math.min(Math.abs(rect.top - probe), Math.abs(rect.bottom - probe))
      };
    });

    var candidates = sceneReads.filter(function (read) {
      return read.scene.intersecting || (read.rect.bottom > 0 && read.rect.top < viewportHeight);
    });
    if (!candidates.length) candidates = sceneReads;
    candidates.sort(function (a, b) {
      if (a.distance !== b.distance) return a.distance - b.distance;
      return b.scene.ratio - a.scene.ratio;
    });
    var nextActive = candidates.length ? candidates[0].scene : null;

    var methodReads = this.methodRoots.map(function (method) {
      var steps = queryAll(method, "[data-amana-method-step], .amana-method__step");
      var activationY = viewportHeight * 0.5;
      var stepReads = steps.map(function (step) {
        var rect = step.getBoundingClientRect();
        var center = rect.top + rect.height * 0.5;
        return { step: step, rect: rect, distance: Math.abs(center - activationY) };
      });
      stepReads.sort(function (a, b) { return a.distance - b.distance; });
      return { method: method, active: stepReads.length ? stepReads[0].step : null };
    });

    var circleReads = this.circleRoots.map(function (circle) {
      var steps = queryAll(circle, ".amana-circle__step, [data-amana-circle-step]");
      var line = viewportHeight * 0.62;
      var passed = 0;
      var closest = null;
      var closestDistance = Infinity;
      steps.forEach(function (step) {
        var rect = step.getBoundingClientRect();
        if (rect.top <= line) passed += 1;
        var distance = Math.abs(rect.top + rect.height * 0.5 - line);
        if (distance < closestDistance) {
          closest = step;
          closestDistance = distance;
        }
      });
      return { circle: circle, steps: steps, passed: passed, closest: closest };
    });

    // Write phase.
    // A fast wheel or a direct anchor jump can cross an element between two
    // IntersectionObserver samples. Anything that has reached the reveal line
    // is therefore considered read, including content already passed above it.
    revealReads.forEach(function (read) {
      if (read.rect.top > viewportHeight * 0.94) return;
      read.element.classList.add("is-amana-revealed");
      if (this.revealObserver) this.revealObserver.unobserve(read.element);
    }, this);

    sceneReads.forEach(function (read) {
      var progress = read.progress;
      var threadProgress = this.policy.continuousAllowed
        ? clamp((progress - 0.06) / 0.76, 0, 1)
        : 1;
      read.scene.root.style.setProperty("--amana-scene-progress", progress.toFixed(5));
      read.scene.root.style.setProperty("--amana-local-progress", progress.toFixed(5));
      read.scene.root.style.setProperty("--amana-thread-progress", threadProgress.toFixed(5));
      read.scene.paths.forEach(function (path) {
        path.style.strokeDasharray = "1";
        path.style.strokeDashoffset = (1 - threadProgress).toFixed(5);
        path.style.setProperty("--amana-thread-progress", threadProgress.toFixed(5));
      });

      if (read.scene.name === "reading" || read.scene.root.matches(".amana-article")) {
        var readingRange = Math.max(1, read.rect.height - viewportHeight + headerHeight);
        var readingProgress = clamp((headerHeight - read.rect.top) / readingRange, 0, 1);
        read.scene.root.style.setProperty("--amana-reading-progress", readingProgress.toFixed(5));
        queryAll(read.scene.root, "[data-amana-reading-progress]").forEach(function (indicator) {
          indicator.style.setProperty("--amana-reading-progress", readingProgress.toFixed(5));
        });
      }
    }, this);

    if (nextActive !== this.activeScene) this.setActiveScene(nextActive);

    methodReads.forEach(function (read) {
      var focused = read.method.contains(document.activeElement) && closestElement(document.activeElement, "[data-amana-method-step], .amana-method__step");
      if (!focused && read.active) this.setMethodStep(read.method, read.active, false);
    }, this);

    circleReads.forEach(function (read) {
      var progress = read.steps.length ? clamp(read.passed / read.steps.length, 0, 1) : 0;
      read.circle.style.setProperty("--amana-circle-progress", progress.toFixed(5));
      read.circle.setAttribute("data-amana-circle-step", String(read.passed));
      var progressbar = read.circle.querySelector("[data-amana-circle-progress]");
      if (progressbar) {
        progressbar.setAttribute("aria-valuenow", String(read.passed));
        progressbar.style.setProperty("--amana-circle-progress", progress.toFixed(5));
        var fill = progressbar.querySelector("[data-amana-circle-progress-fill]");
        if (fill) fill.style.setProperty("--amana-circle-progress", progress.toFixed(5));
      }
      read.steps.forEach(function (step) {
        step.classList.toggle("is-amana-current", step === read.closest);
      });
    });
  };

  AmanaOrganicMotion.prototype.setActiveScene = function (scene) {
    this.activeScene = scene;
    this.scenes.forEach(function (candidate) {
      var active = candidate === scene;
      candidate.root.classList.toggle("is-amana-scene-active", active);
      candidate.root.toggleAttribute("data-amana-scene-active", active);
    });

    var name = scene ? scene.name : "";
    var tone = scene ? scene.tone : "";
    this.headers.forEach(function (header) {
      if (name) header.setAttribute("data-amana-active-scene", name);
      else header.removeAttribute("data-amana-active-scene");
      if (tone) header.setAttribute("data-amana-active-tone", tone);
      else header.removeAttribute("data-amana-active-tone");
    });

    var group = routeGroupForScene(name);
    if (
      group === "journal" &&
      name === "reading" &&
      scene &&
      !scene.root.matches(".amana-article") &&
      !/^\/blogs\//.test(window.location.pathname)
    ) {
      group = "";
    }
    this.headerLinks.forEach(function (link) {
      link.classList.toggle("is-current", Boolean(group && linkMatchesGroup(link, group, scene)));
    });
  };

  AmanaOrganicMotion.prototype.updatePointerSprings = function (delta) {
    var keepMoving = false;
    this.scenes.forEach(function (scene) {
      var pointer = scene.pointer;
      var moving = springStep(pointer, pointer.tx, pointer.ty, delta, 132, 19);
      if (moving) keepMoving = true;

      var percentX = 50 + pointer.x * 38;
      var percentY = 50 + pointer.y * 34;
      var offsetX = pointer.x * this.maxDepth;
      var offsetY = pointer.y * this.maxDepth * 0.72;
      scene.root.style.setProperty("--amana-pointer-x", percentX.toFixed(2) + "%");
      scene.root.style.setProperty("--amana-pointer-y", percentY.toFixed(2) + "%");
      scene.root.style.setProperty("--amana-pointer-offset-x", offsetX.toFixed(2) + "px");
      scene.root.style.setProperty("--amana-pointer-offset-y", offsetY.toFixed(2) + "px");

      scene.depthElements.forEach(function (element) {
        var factor = parseNumber(element.__amanaDepthFactor, 0);
        var strength = 4 + Math.min(1, Math.abs(factor) / 0.32) * (this.maxDepth - 4);
        var direction = factor < 0 ? -1 : 1;
        var x = pointer.x * strength * direction;
        var y = pointer.y * strength * 0.72 * direction;
        element.style.setProperty("--amana-depth-x", x.toFixed(2) + "px");
        element.style.setProperty("--amana-depth-y", y.toFixed(2) + "px");
      }, this);

      scene.lights.forEach(function (light) {
        light.style.setProperty("--amana-pointer-x", percentX.toFixed(2) + "%");
        light.style.setProperty("--amana-pointer-y", percentY.toFixed(2) + "%");
        light.style.setProperty("--amana-light-x", percentX.toFixed(2) + "%");
        light.style.setProperty("--amana-light-y", percentY.toFixed(2) + "%");
        light.style.setProperty("--amana-light-shift-x", offsetX.toFixed(2) + "px");
        light.style.setProperty("--amana-light-shift-y", offsetY.toFixed(2) + "px");
      });
    }, this);
    return keepMoving;
  };

  AmanaOrganicMotion.prototype.updateMagneticSprings = function (delta) {
    var keepMoving = false;
    this.magneticStates.forEach(function (state) {
      var moving = springStep(state, state.tx, state.ty, delta, 220, 28);
      state.element.style.setProperty("--amana-magnetic-x", state.x.toFixed(2) + "px");
      state.element.style.setProperty("--amana-magnetic-y", state.y.toFixed(2) + "px");
      if (moving) keepMoving = true;
    });
    return keepMoving;
  };

  AmanaOrganicMotion.prototype.onVisibilityChange = function () {
    html.classList.toggle("amana-organic-page-hidden", document.hidden);
    if (document.hidden) {
      if (this.frameId) window.cancelAnimationFrame(this.frameId);
      this.frameId = 0;
      this.lastFrameTime = 0;
      return;
    }
    this.scrollDirty = true;
    this.requestFrame();
  };

  AmanaOrganicMotion.prototype.handleBlockSelect = function (block) {
    if (!block) return;
    if (block.matches && block.matches(REVEAL_SELECTOR)) block.classList.add("is-amana-revealed");
    queryAll(block, REVEAL_SELECTOR).forEach(function (element) {
      element.classList.add("is-amana-revealed");
    });
    var sceneRoot = block.closest && block.closest(SCENE_SELECTOR);
    if (sceneRoot && this.sceneByRoot.has(sceneRoot)) this.setActiveScene(this.sceneByRoot.get(sceneRoot));
    this.scrollDirty = true;
    this.requestFrame();
  };

  AmanaOrganicMotion.prototype.destroy = function () {
    if (this.destroyed) return;
    this.destroyed = true;
    if (this.frameId) window.cancelAnimationFrame(this.frameId);
    this.frameId = 0;
    if (this.sceneObserver) this.sceneObserver.disconnect();
    if (this.revealObserver) this.revealObserver.disconnect();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.stageObserver) this.stageObserver.disconnect();
    this.cleanups.splice(0).forEach(function (cleanup) { cleanup(); });

    html.classList.remove(
      "amana-organic-enabled",
      "amana-organic-reduced",
      "amana-organic-static",
      "amana-organic-pointer-ready",
      "amana-organic-design-mode",
      "amana-organic-ready",
      "amana-organic-page-hidden"
    );

    this.scenes.forEach(function (scene) {
      scene.root.classList.remove("is-amana-scene-visible", "is-amana-scene-active", "is-amana-pointer-active");
      scene.root.removeAttribute("data-amana-scene-active");
      removeOwnedStyles(scene.root);
      scene.depthElements.forEach(function (element) {
        delete element.__amanaDepthFactor;
        removeOwnedStyles(element);
      });
      scene.lights.forEach(removeOwnedStyles);
    });

    this.headers.forEach(function (header) {
      header.removeAttribute("data-amana-active-scene");
      header.removeAttribute("data-amana-active-tone");
    });
    this.headerLinks.forEach(function (link) { link.classList.remove("is-current"); });

    this.revealElements.forEach(function (element) {
      element.classList.remove("is-amana-revealed");
      element.style.removeProperty("--amana-organic-delay");
    });
    this.modifiedRevealAttributes.forEach(function (element) {
      element.removeAttribute("data-amana-reveal-variant");
    });

    this.pathSnapshots.forEach(function (snapshot) {
      if (snapshot.pathLength === null) snapshot.path.removeAttribute("pathLength");
      else snapshot.path.setAttribute("pathLength", snapshot.pathLength);
      snapshot.path.style.strokeDasharray = snapshot.dasharray;
      snapshot.path.style.strokeDashoffset = snapshot.dashoffset;
      snapshot.path.style.removeProperty("--amana-thread-progress");
    });
    this.progressSnapshots.forEach(function (snapshot) {
      if (snapshot.valueNow === null) snapshot.element.removeAttribute("aria-valuenow");
      else snapshot.element.setAttribute("aria-valuenow", snapshot.valueNow);
      removeOwnedStyles(snapshot.element);
      queryAll(snapshot.element, "[data-amana-phase-progress-fill], [data-amana-circle-progress-fill], [data-amana-signup-progress-fill]").forEach(removeOwnedStyles);
    });

    queryAll(document, MAGNETIC_SELECTOR).forEach(function (element) {
      element.classList.remove("is-amana-magnetic-ready", "is-amana-magnetic-active");
      removeOwnedStyles(element);
    });
    queryAll(document, INSPECT_SELECTOR).forEach(function (element) {
      element.classList.remove("is-amana-inspected");
      element.removeAttribute("data-amana-inspect-focus");
      removeOwnedStyles(element);
    });
    queryAll(document, JOURNAL_CARD_SELECTOR).forEach(function (card) {
      card.classList.remove("is-amana-preview-active", "is-amana-preview-muted");
    });
    this.journalRoots.forEach(function (root) {
      root.classList.remove("has-amana-journal-preview");
      root.removeAttribute("data-amana-journal-active");
      removeOwnedStyles(root);
    });
    this.methodRoots.forEach(function (method) {
      method.classList.remove("has-amana-focus-step");
      method.removeAttribute("data-amana-active-step");
      removeOwnedStyles(method);
      queryAll(method, "[data-amana-method-step], .amana-method__step").forEach(function (step) {
        step.classList.remove("is-amana-scroll-active", "is-amana-current");
      });
    });
    this.circleRoots.forEach(function (circle) {
      circle.removeAttribute("data-amana-circle-step");
      removeOwnedStyles(circle);
      queryAll(circle, ".amana-circle__step, [data-amana-circle-step]").forEach(function (step) {
        step.classList.remove("is-amana-current");
      });
    });
    this.formRoots.forEach(function (signup) {
      signup.classList.remove("has-amana-form-focus");
      signup.removeAttribute("data-amana-form-focus");
      signup.removeAttribute("data-amana-signup-progress-step");
      removeOwnedStyles(signup);
      queryAll(signup, "form").forEach(removeOwnedStyles);
    });
  };

  var controller = null;
  var runtimeCleanups = [];
  var refreshQueued = false;
  var booted = false;

  function refresh() {
    refreshQueued = false;
    if (!booted) return;
    if (controller) controller.destroy();
    controller = new AmanaOrganicMotion();
  }

  function scheduleRefresh() {
    if (refreshQueued) return;
    refreshQueued = true;
    Promise.resolve().then(refresh);
  }

  function onSectionMutation() {
    scheduleRefresh();
  }

  function onBlockSelect(event) {
    if (controller) controller.handleBlockSelect(event.target);
  }

  function boot() {
    if (booted) return;
    booted = true;
    controller = new AmanaOrganicMotion();
    addMediaListener(runtimeCleanups, reducedQuery, scheduleRefresh);
    addMediaListener(runtimeCleanups, finePointerQuery, scheduleRefresh);
    if (connection) addListener(runtimeCleanups, connection, "change", scheduleRefresh);
    addListener(runtimeCleanups, document, "shopify:section:load", onSectionMutation);
    addListener(runtimeCleanups, document, "shopify:section:unload", onSectionMutation);
    addListener(runtimeCleanups, document, "shopify:section:reorder", onSectionMutation);
    addListener(runtimeCleanups, document, "shopify:block:select", onBlockSelect);
    addListener(runtimeCleanups, document, "shopify:block:deselect", onBlockSelect);
  }

  var runtime = {
    destroy: function () {
      booted = false;
      refreshQueued = false;
      if (controller) controller.destroy();
      controller = null;
      runtimeCleanups.splice(0).forEach(function (cleanup) { cleanup(); });
    },
    refresh: scheduleRefresh,
    get controller() {
      return controller;
    }
  };
  window[RUNTIME_KEY] = runtime;

  if (document.readyState === "loading") {
    var onReady = function () {
      document.removeEventListener("DOMContentLoaded", onReady);
      boot();
    };
    document.addEventListener("DOMContentLoaded", onReady);
    runtimeCleanups.push(function () {
      document.removeEventListener("DOMContentLoaded", onReady);
    });
  } else {
    boot();
  }
})();
